import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const zones = [
  {
    id: "low",
    label: "Below 2.0 pCi/L",
    title: "Low — outdoor baseline range",
    body: "No action needed. We still recommend a retest every couple of years, or after major renovations.",
    color: "text-sky-600",
  },
  {
    id: "caution",
    label: "2.0 – 4.0 pCi/L",
    title: "Caution — monitor the situation",
    body: "Below the EPA action level, but worth a conversation. We'll help you decide whether to retest, monitor, or mitigate.",
    color: "text-amber-600",
  },
  {
    id: "action",
    label: "4.0+ pCi/L",
    title: "EPA action level — mitigate",
    body: "The EPA recommends fixing your home at or above 4.0 pCi/L. This is exactly what our mitigation systems are built for.",
    color: "text-red-600",
  },
];

const zoneFor = (v) => (v < 2 ? zones[0] : v < 4 ? zones[1] : zones[2]);

export default function RadonScale() {
  const [value, setValue] = useState(4.6);
  const zone = zoneFor(value);

  return (
    <section id="radon-scale" className="bg-limestone py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold-deep">
              Know your number
            </p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Radon is measured in pCi/L. What do the numbers mean?
            </h2>
            <p className="mt-4 text-slate-600">
              Radon is invisible and odorless — the only way to know your level
              is to test. Drag the slider to see how the EPA reads the results.
            </p>
            <p className="mt-6 border-l-2 border-gold-deep pl-4 text-sm leading-relaxed text-slate-500">
              The EPA recommends fixing homes at or above 4.0 pCi/L. No level
              of radon is completely safe — but high levels are highly fixable.
            </p>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    Your radon level
                  </p>
                  <p
                    data-testid="radon-scale-value"
                    className="mt-1 font-display text-4xl font-bold text-navy"
                  >
                    {value.toFixed(1)}{" "}
                    <span className="text-base font-semibold text-slate-500">pCi/L</span>
                  </p>
                </div>
                <span
                  data-testid="radon-scale-result"
                  className={`rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold ring-1 ring-slate-200 ${zone.color}`}
                >
                  {zone.label}
                </span>
              </div>

              <input
                data-testid="radon-scale-slider"
                type="range"
                min="0"
                max="10"
                step="0.1"
                value={value}
                onChange={(e) => setValue(parseFloat(e.target.value))}
                className="mt-6 h-2 w-full cursor-pointer appearance-none rounded-full"
                style={{
                  background: `linear-gradient(to right, #0284C7 0%, #0284C7 20%, #F59E0B 20%, #F59E0B 40%, #EF4444 40%, #EF4444 100%)`,
                }}
                aria-label="Radon level in pCi/L"
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-400">
                <span>0</span>
                <span>2</span>
                <span>4</span>
                <span>10+</span>
              </div>

              <motion.div
                key={zone.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="mt-6 w-full rounded-xl bg-slate-50 p-5"
              >
                <p className={`font-display text-lg font-semibold ${zone.color}`}>
                  {zone.title}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  {zone.body}
                </p>
              </motion.div>

              <a
                data-testid="radon-scale-cta"
                href="#booking"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-deep"
              >
                Test my home
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
